import type {
    CSSObject,
    Components,
    ComponentsOverrides,
    ComponentsPropsList,
    Theme,
} from '@mui/material/styles';

import type { SettingsState } from '../../components/settings/types';
import type { ThemeOptions } from '../types';

type ComponentSlot<
    Name extends keyof ComponentsOverrides<Theme>,
    Slot extends keyof NonNullable<ComponentsOverrides<Theme>[Name]>,
> = NonNullable<ComponentsOverrides<Theme>[Name]>[Slot];

/**
 * Retrieves styles for a specific component slot.
 *
 * @template Name - The name of the component in the ComponentsOverrides.
 * @template Slot - The name of the slot within the component.
 *
 * @param {ComponentSlot<Name, Slot>} slot - The slot to get styles from, can be a function or an object.
 * @param {ComponentsPropsList[Name]} [props] - Optional props to pass to the slot if it's a function.
 *
 * @returns {CSSObject} The styles for the specified slot.
 *
 * @description
 * If the slot is a function and props are provided, it calls the function with the props.
 * Otherwise, it returns the slot object or an empty object if the slot is null or undefined.
 */
function getSlotStyles<
    Name extends keyof ComponentsOverrides<Theme>,
    Slot extends keyof NonNullable<ComponentsOverrides<Theme>[Name]>,
>(slot: ComponentSlot<Name, Slot>, props?: ComponentsPropsList[Name]): CSSObject {
    const slotStyles = typeof slot === 'function' && props ? slot(props) : slot ?? {};

    return slotStyles;
}

/**
 * Updates the components of the theme with user settings.
 *
 * This function modifies the appearance of Material UI components based on the user's settings,
 * such as contrast level and font size.
 *
 * @param components - The original components configuration from the theme
 * @param settingsState - The current user settings state
 * @returns A partial ThemeOptions object with the updated components configuration
 *
 * @example
 * const updatedTheme = updateComponentsWithSettings(theme.components, userSettings);
 * const finalTheme = createTheme({
 *   ...theme,
 *   ...updatedTheme
 * });
 */
export function updateComponentsWithSettings(
    components?: Components<Theme>,
    settingsState?: SettingsState,
): Pick<ThemeOptions, 'components'> {
    const MuiCard: Components<Theme>['MuiCard'] = {
        styleOverrides: {
            root: (props) => {
                const { theme } = props;

                const rootStyles = getSlotStyles<'MuiCard', 'root'>(
                    components?.MuiCard?.styleOverrides?.root,
                    props,
                );

                return {
                    ...rootStyles,
                    ...(settingsState?.contrast === 'hight' && {
                        boxShadow: theme.vars.customShadows.z1,
                    }),
                };
            },
        },
    };

    const MuiCssBaseline: Components<Theme>['MuiCssBaseline'] = {
        styleOverrides: {
            html: {
                fontSize: settingsState?.fontSize,
            },
        },
    };

    return {
        components: {
            MuiCard,
            MuiCssBaseline,
        },
    };
}
